/**
 * F-150: the step tasks a file import leaves in the Agent panel. The import
 * itself only copies the file in; what used to happen in one go — cut the
 * script into beats, translate it, divide the other columns across the
 * beats — waits here as one button per step. Each button proposes its step
 * as a plan (`runImportStep`), the person applies it like any agent plan,
 * and the button goes. Reverting a step's plan brings its button back.
 *
 * Kept per board in localStorage, so the buttons are still there after a
 * reload — and with them the beat-row GROUPS the split made, which "Align"
 * needs (the board alone cannot say which rows came from one file row).
 *
 * PURE apart from storage: the controller decides when the state changes.
 */
import { IMPORT_STEP_TASKS, isImportStepTask, type AgentPlan, type ImportStepTask } from "@kola/shared";

import type { AppliedRun } from "./apply.js";

export interface ImportTasks {
  fileName: string;
  sheetId: string;
  /** Steps still to do, in the owner's order. Empty = all done (the bar hides). */
  pending: ImportStepTask[];
  /** From the applied split: each split row and its beat rows, `[rowId, …]`. */
  groups?: string[][];
}

export const IMPORT_STEP_LABELS: Record<ImportStepTask, string> = {
  beats: "Split into beats",
  translate: "Translate to English",
  align: "Align visual, sound & extras",
};

/** What the transcript says the person asked for. */
export const IMPORT_STEP_MESSAGES: Record<ImportStepTask, string> = {
  beats: "Split the script into beats — a few words or one short sentence per row.",
  translate: "Translate the script into a new English column, one translation per row.",
  align: "Divide the visual, sound and extra columns across each row's beats.",
};

/** The tasks a freshly applied import starts with. */
export function freshImportTasks(fileName: string, sheetId: string): ImportTasks {
  return { fileName, sheetId, pending: [...IMPORT_STEP_TASKS] };
}

/**
 * Why a step's button is disabled, or null when it can run. Translating
 * and aligning follow the split: the owner's order is one translation per
 * beat row, and there is nothing to align before there are beats.
 */
export function importStepBlocked(tasks: ImportTasks, step: ImportStepTask): string | null {
  return step !== "beats" && tasks.pending.includes("beats") ? "Split into beats first" : null;
}

export function withStepDone(tasks: ImportTasks, step: ImportStepTask, groups?: string[][]): ImportTasks {
  const next: ImportTasks = { ...tasks, pending: tasks.pending.filter((s) => s !== step) };
  if (groups !== undefined) next.groups = groups;
  return next;
}

export function withStepPending(tasks: ImportTasks, step: ImportStepTask): ImportTasks {
  const pending = IMPORT_STEP_TASKS.filter((s) => s === step || tasks.pending.includes(s));
  const next: ImportTasks = { ...tasks, pending };
  if (step === "beats") delete next.groups;
  return next;
}

/**
 * The split's groups as they landed: `importGroups` names one split row per
 * `insert_rows` op, in op order, and the run created exactly those rows in
 * that order. Undefined when the counts disagree — then nothing is guessed.
 */
export function groupsFromRun(plan: AgentPlan, run: AppliedRun): string[][] | undefined {
  const groups = plan.importGroups ?? [];
  const total = groups.reduce((sum, group) => sum + group.added, 0);
  if (total !== run.createdRowIds.length) return undefined;
  let next = 0;
  return groups.map((group) => {
    const ids = run.createdRowIds.slice(next, next + group.added);
    next += group.added;
    return [group.rowId, ...ids];
  });
}

/* ------------------------------- storage -------------------------------- */

const storageKey = (boardId: string) => `kola.importTasks.${boardId}`;

export function loadImportTasks(boardId: string): ImportTasks | null {
  try {
    const raw = localStorage.getItem(storageKey(boardId));
    return raw === null ? null : parseImportTasks(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function saveImportTasks(boardId: string, tasks: ImportTasks | null): void {
  try {
    if (tasks === null) localStorage.removeItem(storageKey(boardId));
    else localStorage.setItem(storageKey(boardId), JSON.stringify(tasks));
  } catch {
    // Storage unavailable — the buttons last as long as this board view.
  }
}

function parseImportTasks(value: unknown): ImportTasks | null {
  if (typeof value !== "object" || value === null) return null;
  const record = value as Record<string, unknown>;
  if (typeof record["fileName"] !== "string" || typeof record["sheetId"] !== "string") return null;
  const pending = Array.isArray(record["pending"]) ? record["pending"].filter(isImportStepTask) : [];
  const tasks: ImportTasks = { fileName: record["fileName"], sheetId: record["sheetId"], pending };
  const groups = record["groups"];
  if (
    Array.isArray(groups) &&
    groups.every((group) => Array.isArray(group) && group.every((id) => typeof id === "string"))
  ) {
    tasks.groups = groups as string[][];
  }
  return tasks;
}
